import '@shui/core/sh-input-text';

import {
    booleanAttribute,
    Component,
    input,
    output,
    ChangeDetectionStrategy,
    inject,
    ElementRef,
    afterEveryRender
} from '@angular/core';

@Component({
    selector: 'sh-input-text',
    template: '<ng-content />',
    imports: [],
    changeDetection: ChangeDetectionStrategy.Eager,
    host: {
        '[attr.accessible-clear-label]': 'accessibleClearLabel() ?? null',
        '[attr.autocomplete]': 'autocomplete() ?? null',
        '[attr.condensed]': 'condensed() ? "" : null',
        '[attr.disabled]': 'disabled() ? "" : null',
        '[attr.error]': 'error() ? "" : null',
        '[attr.error-message]': 'errorMessage() ?? null',
        '[attr.hint]': 'hint() ?? null',
        '[attr.isAutofocus]': 'isAutofocus() ? "" : null',
        '[attr.label]': 'label() ?? null',
        '[attr.mandatory]': 'mandatory() ? "" : null',
        '[attr.maxlength]': 'maxlength() ?? null',
        '[attr.no-border]': 'noBorder() ? "" : null',
        '[attr.no-clear]': 'noClear() ? "" : null',
        '[attr.readonly]': 'readonly() ? "" : null',
        '[attr.resizable]': 'resizable() ? "" : null',
        '[attr.rows]': 'rows() ?? null',
        '[attr.safety]': 'safety() ? "" : null',
        '[attr.success]': 'success() ? "" : null',
        '[attr.textarea]': 'textarea() ? "" : null',
        '[attr.type]': 'type() ?? null',
        '[attr.value]': 'value() ?? null',
        '[attr.warning]': 'warning() ? "" : null',
        '(clear-clicked)': 'clearClicked.emit($event)',
        '(value-changed)': 'valueChanged.emit($event)'
    }
})
export class ShInputTextComponent {
    private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);

    /**
     * If the `accessibleClearLabel` property is not set, the icon will use the `type` to
     * dynamically set the `label`.
     */
    public readonly accessibleClearLabel = input<string | number | undefined>(undefined);

    /**
     * Specifies the autocomplete behavior for the input field. It accepts standard autocomplete
     * attribute values defined by HTML.
     */
    public readonly autocomplete = input<string | number | undefined>(undefined);

    /**
     * When true, renders the input in a compact format for space-constrained layouts. Commonly used
     * in tables where labels can be omitted if the table header provides context. To ensure
     * accessibility, use alternative labeling methods (e.g., aria-label) when the label is hidden!
     */
    public readonly condensed = input(false, { transform: booleanAttribute });

    /** If set to `true`, input won't be allowed and disabled styles will be applied. */
    public readonly disabled = input(false, { transform: booleanAttribute });

    /** If set to `true`, the input text displays styles for invalid input. */
    public readonly error = input(false, { transform: booleanAttribute });

    /**
     * Displays a message when error property is set to true and when error-message is not
     * undefined.
     */
    public readonly errorMessage = input<string | number | undefined>(undefined);

    /** Displays a message below the input box. */
    public readonly hint = input<string | number | undefined>(undefined);

    /**
     * If set to `true`, the first sh-input-text having this attribute as true, will have focus on
     * page load. Behaviour similar to autofocus attribute of HTML5 text input. Note: There is a
     * known limitation in Safari on iOS where autofocus is not maintained after the keyboard
     * appears. This behavior is consistent across iOS devices and versions as iOS doesn't support
     * autofocus.
     */
    public readonly isAutofocus = input(false, { transform: booleanAttribute });

    /** Defines the label of the input. If the value is empty, it will be shown as a placeholder. */
    public readonly label = input<string | number | undefined>(undefined);

    /** If set to `true`, the input text displays styles for mandatory input. */
    public readonly mandatory = input(false, { transform: booleanAttribute });

    /** Defines the maximum length allowed for the text (in number of letters). */
    public readonly maxlength = input<string | number | undefined>(undefined);

    /** If set to `true`, the bottom border will not be displayed. */
    public readonly noBorder = input(false, { transform: booleanAttribute });

    /** If set to `true`, no clear button is displayed. */
    public readonly noClear = input(false, { transform: booleanAttribute });

    /**
     * The `readonly` attribute controls user input on the field. When `readonly` is set to true,
     * the field becomes non-interactive and does not accept input. If the `readonly` attribute is
     * not present, the field remains editable, allowing user input.
     */
    public readonly readonly = input(false, { transform: booleanAttribute });

    /** If set to `true`, the resize will be enabled for textarea. */
    public readonly resizable = input(false, { transform: booleanAttribute });

    /** Only if textarea is true, this will define the amount of rows shown by default. */
    public readonly rows = input<string | number | undefined>(undefined);

    /**
     * If set to `true`, the input text displays styles for safety-relevant input (yellow outline).
     */
    public readonly safety = input(false, { transform: booleanAttribute });

    /** If set to `true`, the input text displays styles for valid input. */
    public readonly success = input(false, { transform: booleanAttribute });

    /**
     * If set to `true`, the input will behave like a textarea, which means will allow multiple rows
     * of text and vertical resizing .
     */
    public readonly textarea = input(false, { transform: booleanAttribute });

    /** If set to password, the value will be shown as dots. */
    public readonly type = input<string | number | undefined>(undefined);

    /**
     * Represents the current value of the input. Can be used for data binding and changes upon user
     * input.
     */
    public readonly value = input<string | number | undefined>(undefined);

    /**
     * If set to `true`, the input text displays styles for warning input (tinted background +
     * yellow border + warning icon).
     */
    public readonly warning = input(false, { transform: booleanAttribute });

    public readonly clearClicked = output<Event>();

    public readonly valueChanged = output<Event>();

    constructor() {
        afterEveryRender(() => this.syncClearButtonAccessibility());
    }

    private syncClearButtonAccessibility(): void {
        setTimeout(() => {
            this.elementRef.nativeElement.shadowRoot
                ?.querySelector<HTMLElement>('#clear')
                ?.setAttribute('tabindex', '0');
        });
    }
}
